import { Box, Popover, PopoverArrow, PopoverContent, PopoverTrigger, Portal, Spinner } from "@chakra-ui/react";
import { AuthContext } from "contexts/AuthContext";
import { useStorage } from "hooks/useStorage";
import { useContext, useEffect, useRef, useState } from "react";

export default function Thumbnail({ content }) {
  const { user } = useContext(AuthContext)
  const { getUrl } = useStorage({ user })

  const [source, setSource] = useState()

  // Load the file only once the thumbnail is on screen: the selection lists can show
  // up to 200 items per page, and 200 downloads / video elements at once is too much.
  const boxRef = useRef(null)
  const [visible, setVisible] = useState(typeof IntersectionObserver === 'undefined')

  useEffect(() => {
    if (visible || !boxRef.current) return
    const observer = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting)) {
        setVisible(true)
        observer.disconnect()
      }
    }, { rootMargin: '200px' })
    observer.observe(boxRef.current)
    return () => observer.disconnect()
  }, [visible])

  useEffect(() => {
    if (!visible) return
    let active = true
    getUrl(content)
      .then(url => active && setSource(url))
      .catch(() => active && setSource(null))
    return () => { active = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content.id, visible])

  const isImage = content.type === 'image'

  // Render the media. `big` enables muted autoplay in the hover preview;
  // the small thumbnail shows a seeked frame so videos aren't just black.
  const media = (big) => {
    if (source === undefined) {
      return <Spinner size='sm' color='gray.400' />
    }
    if (!source) {
      return null
    }
    if (isImage) {
      return <img src={source} alt='' style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
    }
    return big
      ? <video src={source} autoPlay muted loop preload='auto' style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000' }} />
      : <video src={source + '#t=0.5'} muted preload='metadata' style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
  }

  return (
    <Popover trigger='hover' placement='right' isLazy openDelay={150}>
      <PopoverTrigger>
        <Box
          ref={boxRef}
          w='44px' h='44px'
          borderRadius='0.375rem'
          overflow='hidden'
          bg='gray.100'
          display='flex'
          alignItems='center'
          justifyContent='center'
          cursor='pointer'>
          {media(false)}
        </Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent w='360px' h='270px' overflow='hidden' borderRadius='0.5rem'>
          <PopoverArrow />
          <Box w='100%' h='100%' bg='gray.100' display='flex' alignItems='center' justifyContent='center'>
            {media(true)}
          </Box>
        </PopoverContent>
      </Portal>
    </Popover>
  )
}
